import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { isKnown } from "../../config/legal";

// Conteneur de page publique/légale : largeur lisible + animation d'entrée discrète.
export const Page = ({ children, testid }) => (
  <motion.div
    data-testid={testid}
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    className="max-w-3xl mx-auto py-4"
  >
    {children}
  </motion.div>
);

export const H1 = ({ children }) => (
  <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">{children}</h1>
);

export const Lead = ({ children }) => (
  <p className="text-base text-slate-600 leading-relaxed mb-6">{children}</p>
);

export const H2 = ({ children, id }) => (
  <h2 id={id} className="font-heading text-xl font-bold mt-10 mb-2 text-slate-900 scroll-mt-32">{children}</h2>
);

export const H3 = ({ children }) => (
  <h3 className="font-heading text-base font-semibold mt-5 mb-1 text-slate-800">{children}</h3>
);

export const P = ({ children }) => (
  <p className="text-sm text-slate-600 leading-relaxed mb-3">{children}</p>
);

export const UL = ({ children }) => (
  <ul className="text-sm text-slate-600 leading-relaxed mb-3 list-disc pl-5 space-y-1">{children}</ul>
);

// Encadré « travail à valider juridiquement » / avertissement non bloquant.
export const Notice = ({ children, testid, tone = "amber" }) => {
  const tones = {
    amber: "border-amber-300 bg-amber-50/70 text-amber-800",
    emerald: "border-emerald-300 bg-emerald-50/70 text-emerald-800",
    slate: "border-slate-200 bg-slate-50 text-slate-600",
  };
  return (
    <div data-testid={testid} className={`rounded-xl border px-4 py-3 text-sm my-5 ${tones[tone] || tones.amber}`}>
      {children}
    </div>
  );
};

// Marqueur explicite d'information légale manquante (jamais de valeur inventée).
export const Placeholder = () => {
  const { t } = useTranslation("public");
  return (
    <span data-testid="legal-placeholder" className="text-amber-600 italic font-medium">
      {t("legalCommon.placeholder")}
    </span>
  );
};

// Pays de l'éditeur et numéro de version « de travail » : libellés issus du language pack.
export const PublisherCountry = () => useTranslation("public").t("legalCommon.publisherCountry");
export const DocVersion = ({ value }) => useTranslation("public").t("legalCommon.draftVersion", { version: value });

// Affiche une valeur légale si elle est connue, sinon le placeholder explicite.
export const LegalValue = ({ value }) => (isKnown(value) ? <span>{value}</span> : <Placeholder />);

// Sommaire léger basé sur les ancres existantes (navigation rapide, sans JS complexe).
export const Toc = ({ items }) => {
  const { t } = useTranslation("public");
  return (
  <nav data-testid="page-toc" aria-label={t("legalCommon.toc")} className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 mb-8">
    <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{t("legalCommon.toc")}</div>
    <div className="flex flex-wrap gap-x-4 gap-y-1.5">
      {items.map((i) => (
        <a
          key={i.id}
          href={`#${i.id}`}
          className="text-sm text-emerald-700 hover:text-emerald-500 underline-offset-2 hover:underline"
        >
          {i.label}
        </a>
      ))}
    </div>
  </nav>
  );
};

// Petite carte illustrée (pages Sécurité / Trust).
export const InfoCard = ({ icon: Icon, title, children, testid }) => (
  <div data-testid={testid} className="glass rounded-xl p-5">
    <div className="w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-3">
      <Icon className="w-5 h-5 text-emerald-600" />
    </div>
    <h3 className="font-heading font-semibold mb-1 text-slate-900">{title}</h3>
    <p className="text-sm text-slate-500 leading-relaxed">{children}</p>
  </div>
);
